import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOnHoverOutside } from 'src/hooks/useOnHoverOutside';
import { NavIconWithPopoverProps } from '../NavLink.types';
import {
  LinksMenu,
  MenuWrapper,
  NavIconWithPopoverContainer,
  getNavIconWithPopoverStyles,
} from './NavIconWithPopover.styles';
import { ExternalLink, Text, Flex } from 'src/packages/core';
import { ThemeProvider, DarkTheme } from 'src/packages/theme';
import { goToUrl } from 'src/utils/go-to-url';

const NavIconWithPopover: React.FC<NavIconWithPopoverProps> = ({
  icon: Icon,
  headerIcon: HeaderIcon,
  headerText,
  links = [],
  mainLink,
  popoverCustomContent = undefined,
  noPopover = false,
  noPointer = false,
  theme = 'dark',
}: NavIconWithPopoverProps) => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [isMenuDropDownOpen, setMenuDropDownOpen] = useState(false);

  const closeHoverMenu = () => {
    setMenuDropDownOpen(false);
  };

  useOnHoverOutside(dropdownRef, closeHoverMenu);

  const { menuItemBgColorHover, popoverMenuBgColor, menuItemLabelColor, iconColor } =
    useMemo(() => {
      return getNavIconWithPopoverStyles(theme);
    }, [theme]);

  const renderHeaderItem = () => {
    if (!headerText && !HeaderIcon) {
      return null;
    }

    const textToShow = headerText?.slice(0, -8);

    return (
      <ThemeProvider>
        <Flex
          alignItems={'center'}
          py={3}
          px={2}
          borderBottom={`1px solid ${menuItemBgColorHover}`}
          width={'100%'}
          backgroundColor={popoverMenuBgColor}
        >
          {HeaderIcon && <HeaderIcon color={iconColor} />}
          {textToShow && (
            <li>
              <Text
                variant={'p2'}
                text={`${textToShow}...`}
                fontFamily={'Inter-Bold'}
                ml={1}
                color={menuItemLabelColor}
              />
            </li>
          )}
        </Flex>
      </ThemeProvider>
    );
  };

  const renderPopoverContent = useMemo(() => {
    if (popoverCustomContent) {
      return (
        <MenuWrapper>
          <LinksMenu
            popoverMenuBgColor={popoverMenuBgColor}
            menuItemBgColorHover={menuItemBgColorHover}
          >
            {popoverCustomContent}
          </LinksMenu>
        </MenuWrapper>
      );
    } else {
      return (
        <MenuWrapper>
          <LinksMenu
            popoverMenuBgColor={popoverMenuBgColor}
            menuItemBgColorHover={menuItemBgColorHover}
          >
            {renderHeaderItem()}
            {links.map((item, index) => (
              <ExternalLink key={index} link={item.url && item.url}>
                <Text variant={'p2'} text={t(item.label)} color={menuItemLabelColor} />
              </ExternalLink>
            ))}
          </LinksMenu>
        </MenuWrapper>
      );
    }
  }, [popoverCustomContent]);

  const handleClick = () => {
    if (mainLink) {
      goToUrl(mainLink);
    }
  };

  return (
    <NavIconWithPopoverContainer ref={dropdownRef} nopointer={noPointer}>
      {noPopover && (
        <div className={'nav-icon'}>
          {Icon && <Icon size={20} color={DarkTheme.colors.palette.white} />}
        </div>
      )}

      {!noPopover && (
        <>
          <div
            className={'nav-icon'}
            onMouseOver={() => setMenuDropDownOpen(true)}
            onClick={handleClick}
          >
            {Icon && <Icon size={20} color={DarkTheme.colors.palette.white} />}
          </div>

          {isMenuDropDownOpen && renderPopoverContent}
        </>
      )}
    </NavIconWithPopoverContainer>
  );
};

export default NavIconWithPopover;
